import classNames from 'classnames';
import {
  ArrowRight,
  Check,
  Clock3,
  Coins,
  Flame,
  Gamepad2,
  Gift,
  Loader2,
  RefreshCw,
  ScrollText,
  Sparkles,
  Target,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GameContinueStatus } from 'shared/enums/game/game-continue-status.enum';
import { TodayPlanAction } from 'shared/interfaces/engagement/today-plan/today-plan-action.enum';
import { TodayPlanItemDto } from 'shared/interfaces/engagement/today-plan/today-plan-item.dto';
import { TodayPlanResponseDto } from 'shared/interfaces/engagement/today-plan/today-plan-response.dto';
import { dealApi } from 'src/api/deal/deal';
import { todayPlanApi } from 'src/api/engagement/today-plan';
import { gameApi } from 'src/api/game/game';
import { questApi } from 'src/api/quest/quest';
import QuestClaimWarningModal from 'src/components/QuestClaimWarningModal/QuestClaimWarningModal';
import { useQuestClaimGamification } from 'src/contexts/QuestClaimGamificationContext/QuestClaimGamificationContext';
import { useQuestsContext } from 'src/contexts/QuestsContext/QuestsContext';
import { useUserAccountDetailsContext } from 'src/contexts/UserAccountDetailsContext/UserAccountDetailsContext';
import { useUserCurrency } from 'src/hooks/use-user-currency';
import { useVerisoul } from 'src/hooks/use-verisoul';
import { openEntryLink } from 'src/utils/entry-link.util';
import { pushRepeatableDataLayerEvent } from 'src/utils/gtm';
import { GtmEventName } from 'src/utils/gtm-event-name.enum';

const PLAN_CACHE_KEY = 'today-plan:last-response';
const FIRST_SEEN_CACHE_KEY = 'today-plan:first-seen';
const LAST_VARIANT_CACHE_KEY = 'today-plan:last-variant';
const COMPLETION_WINDOW_MS = 24 * 60 * 60 * 1000;
const COMPLETION_ANIMATION_MS = 500;

type TodayPlanProps = {
  onProtectStreak: () => void;
};

type FirstSeenMap = Record<string, number>;

function readSessionJson<T>(key: string): T | null {
  try {
    const rawValue = window.sessionStorage.getItem(key);
    return rawValue ? (JSON.parse(rawValue) as T) : null;
  } catch {
    return null;
  }
}

function writeSessionJson(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage may be disabled. The server remains the source of truth.
  }
}

function getCountdown(expiresAt: string, now: number, expiredLabel: string): string {
  const remainingMs = new Date(expiresAt).getTime() - now;
  if (remainingMs <= 0) {
    return expiredLabel;
  }

  const totalMinutes = Math.max(1, Math.ceil(remainingMs / 60_000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function getActionIcon(action: TodayPlanAction): React.ReactNode {
  const iconClassName = 'h-4.5 w-4.5';
  switch (action) {
    case TodayPlanAction.ClaimChest:
      return <Gift className={iconClassName} />;
    case TodayPlanAction.ClaimQuest:
    case TodayPlanAction.ResumeQuest:
    case TodayPlanAction.ChooseQuest:
      return <ScrollText className={iconClassName} />;
    case TodayPlanAction.ResumeGame:
    case TodayPlanAction.ResumeDeal:
      return <Gamepad2 className={iconClassName} />;
    case TodayPlanAction.ProtectStreak:
      return <Flame className={iconClassName} />;
    case TodayPlanAction.ViewReward:
      return <Sparkles className={iconClassName} />;
  }
}

function getSupportingText(
  item: TodayPlanItemDto,
  t: ReturnType<typeof useTranslation>['t'],
  formatPoints: (points: number) => string
): string | null {
  const values = item.supportingValues;
  if (
    item.action === TodayPlanAction.ResumeQuest &&
    typeof values.progress === 'number' &&
    typeof values.needed === 'number'
  ) {
    return t('COMPONENTS.TODAY_PLAN.SUPPORT.QUEST_PROGRESS', {
      progress: values.progress,
      needed: values.needed,
    });
  }

  if (item.action === TodayPlanAction.ClaimQuest || item.action === TodayPlanAction.ClaimChest) {
    const rewards = [
      typeof values.points === 'number' && values.points > 0
        ? t('COMPONENTS.TODAY_PLAN.SUPPORT.POINTS', { points: formatPoints(values.points) })
        : null,
      typeof values.xp === 'number' && values.xp > 0 ? t('COMPONENTS.TODAY_PLAN.SUPPORT.XP', { xp: values.xp }) : null,
    ].filter(Boolean);
    return rewards.join(' · ') || null;
  }

  if (item.action === TodayPlanAction.ResumeGame || item.action === TodayPlanAction.ResumeDeal) {
    const journeyDetails = [
      typeof values.goalName === 'string' && values.goalName.length > 0
        ? t('COMPONENTS.TODAY_PLAN.SUPPORT.NEXT_GOAL', { goal: values.goalName })
        : null,
      typeof values.pointsRemaining === 'number' && values.pointsRemaining > 0
        ? t('COMPONENTS.TODAY_PLAN.SUPPORT.POINTS_AVAILABLE', { points: formatPoints(values.pointsRemaining) })
        : null,
    ].filter(Boolean);
    return journeyDetails.join(' · ') || null;
  }

  if (item.action === TodayPlanAction.ProtectStreak && typeof values.currentStreak === 'number') {
    return t('COMPONENTS.TODAY_PLAN.SUPPORT.STREAK_DAYS', { days: values.currentStreak });
  }

  if (typeof values.remainingPoints === 'number' && values.remainingPoints > 0) {
    return t('COMPONENTS.TODAY_PLAN.SUPPORT.POINTS_TO_GO', { points: formatPoints(values.remainingPoints) });
  }

  if (item.action === TodayPlanAction.ViewReward && typeof values.points === 'number' && values.points > 0) {
    return t('COMPONENTS.TODAY_PLAN.SUPPORT.POINTS', { points: formatPoints(values.points) });
  }

  if (item.action === TodayPlanAction.ChooseQuest && typeof values.availableSlots === 'number') {
    return t('COMPONENTS.TODAY_PLAN.SUPPORT.QUEST_SLOTS', { count: values.availableSlots });
  }

  return null;
}

export const TodayPlanSkeleton: React.FC = () => (
  <section
    aria-label="Loading today's priorities"
    className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
  >
    <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 md:px-5">
      <div className="h-8 w-8 animate-pulse rounded-xl bg-slate-100" />
      <div className="h-4 w-28 animate-pulse rounded-full bg-slate-100" />
    </div>
    <div className="space-y-3 p-4 md:p-5">
      {[0, 1].map((row) => (
        <div key={row} className="flex items-center gap-3">
          <div className="h-10 w-10 animate-pulse rounded-xl bg-slate-100" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3.5 w-2/3 animate-pulse rounded-full bg-slate-100" />
            <div className="h-3 w-1/3 animate-pulse rounded-full bg-slate-100" />
          </div>
          <div className="h-9 w-24 animate-pulse rounded-xl bg-slate-100" />
        </div>
      ))}
    </div>
  </section>
);

const TodayPlan: React.FC<TodayPlanProps> = ({ onProtectStreak }) => {
  const { t } = useTranslation();
  const { formatPoints } = useUserCurrency();
  const navigate = useNavigate();
  const { getSessionId } = useVerisoul();
  const { presentQuestClaimOutcomes } = useQuestClaimGamification();
  const { questChestProgress, refreshQuests, refreshQuestChestProgress, claimQuestAndRefresh } = useQuestsContext();
  const { refreshAccountDetails } = useUserAccountDetailsContext();
  const cachedPlan = useMemo(() => readSessionJson<TodayPlanResponseDto>(PLAN_CACHE_KEY), []);
  const [plan, setPlan] = useState<TodayPlanResponseDto | null>(cachedPlan);
  const [isLoading, setIsLoading] = useState(!cachedPlan);
  const [hasError, setHasError] = useState(false);
  const [workingActionId, setWorkingActionId] = useState<string | null>(null);
  const [questClaimToConfirm, setQuestClaimToConfirm] = useState<TodayPlanItemDto | null>(null);
  const [completingActionIds, setCompletingActionIds] = useState<Set<string>>(new Set());
  const [now, setNow] = useState(Date.now());
  const planRef = useRef<TodayPlanResponseDto | null>(cachedPlan);
  const moduleImpressionTrackedRef = useRef(false);
  const itemImpressionsTrackedRef = useRef(new Set<string>());

  const getAnalyticsParameters = useCallback((currentPlan: TodayPlanResponseDto, item?: TodayPlanItemDto) => {
    return {
      experiment_name: currentPlan.experiment.name,
      experiment_variant: currentPlan.experiment.variant,
      segment_new_user: currentPlan.experiment.segments.newUser,
      segment_active_games: currentPlan.experiment.segments.activeGames,
      segment_claimable_quests: currentPlan.experiment.segments.claimableQuests,
      segment_close_to_reward: currentPlan.experiment.segments.closeToReward,
      ...(item
        ? {
            today_item_id: item.id,
            today_action: item.action,
            today_source: item.source,
            today_priority: item.displayPriority,
            today_is_primary: item.isPrimary,
          }
        : {}),
    };
  }, []);

  const rememberFirstSeen = useCallback((actions: TodayPlanItemDto[]) => {
    const firstSeen = readSessionJson<FirstSeenMap>(FIRST_SEEN_CACHE_KEY) ?? {};
    const timestamp = Date.now();
    actions.forEach((action) => {
      firstSeen[action.id] ??= timestamp;
    });
    writeSessionJson(FIRST_SEEN_CACHE_KEY, firstSeen);
  }, []);

  const applyPlan = useCallback(
    async (nextPlan: TodayPlanResponseDto, animateRemovedActions: boolean) => {
      const previousPlan = planRef.current;
      const nextIds = new Set(nextPlan.actions.map((action) => action.id));
      const removedActions =
        animateRemovedActions && previousPlan
          ? previousPlan.actions.filter(
              (action) =>
                !nextIds.has(action.id) &&
                !nextPlan.unavailableSources.includes(action.source) &&
                (!action.expiresAt || new Date(action.expiresAt).getTime() > Date.now()) &&
                [
                  TodayPlanAction.ClaimQuest,
                  TodayPlanAction.ClaimChest,
                  TodayPlanAction.ResumeQuest,
                  TodayPlanAction.ResumeGame,
                  TodayPlanAction.ResumeDeal,
                  TodayPlanAction.ProtectStreak,
                ].includes(action.action)
            )
          : [];

      if (removedActions.length > 0) {
        const firstSeen = readSessionJson<FirstSeenMap>(FIRST_SEEN_CACHE_KEY) ?? {};
        const completedWithinWindow = removedActions.filter(
          (action) => firstSeen[action.id] && Date.now() - firstSeen[action.id] <= COMPLETION_WINDOW_MS
        );

        completedWithinWindow.forEach((action) => {
          pushRepeatableDataLayerEvent(GtmEventName.TodayPlanCompletion24h, getAnalyticsParameters(nextPlan, action));
        });

        setCompletingActionIds(new Set(removedActions.map((action) => action.id)));
        await new Promise<void>((resolve) => window.setTimeout(resolve, COMPLETION_ANIMATION_MS));
      }

      planRef.current = nextPlan;
      setPlan(nextPlan);
      setCompletingActionIds(new Set());
      rememberFirstSeen(nextPlan.actions);
      writeSessionJson(PLAN_CACHE_KEY, nextPlan);
      window.sessionStorage.setItem(LAST_VARIANT_CACHE_KEY, nextPlan.experiment.variant);
    },
    [getAnalyticsParameters, rememberFirstSeen]
  );

  const refreshPlan = useCallback(
    async (animateRemovedActions = false) => {
      if (!planRef.current) {
        setIsLoading(true);
      }

      try {
        const nextPlan = await todayPlanApi.getTodayPlan();
        setHasError(false);
        await applyPlan(nextPlan, animateRemovedActions);
      } catch {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [applyPlan]
  );

  useEffect(() => {
    void refreshPlan(false);
  }, [refreshPlan]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const refreshAfterPartnerReturn = () => {
      if (document.visibilityState === 'visible') {
        void refreshPlan(true);
      }
    };

    window.addEventListener('focus', refreshAfterPartnerReturn);
    document.addEventListener('visibilitychange', refreshAfterPartnerReturn);
    return () => {
      window.removeEventListener('focus', refreshAfterPartnerReturn);
      document.removeEventListener('visibilitychange', refreshAfterPartnerReturn);
    };
  }, [refreshPlan]);

  useEffect(() => {
    if (!plan || plan.experiment.variant !== 'today' || plan.actions.length === 0) {
      return;
    }

    if (!moduleImpressionTrackedRef.current) {
      moduleImpressionTrackedRef.current = true;
      pushRepeatableDataLayerEvent(GtmEventName.TodayPlanModuleImpression, getAnalyticsParameters(plan));
    }

    plan.actions.forEach((item) => {
      if (itemImpressionsTrackedRef.current.has(item.id)) {
        return;
      }
      itemImpressionsTrackedRef.current.add(item.id);
      pushRepeatableDataLayerEvent(GtmEventName.TodayPlanItemImpression, getAnalyticsParameters(plan, item));
    });
  }, [getAnalyticsParameters, plan]);

  useEffect(() => {
    if (!plan) {
      return;
    }

    const expiredAction = plan.actions.find(
      (action) => action.expiresAt && new Date(action.expiresAt).getTime() <= now
    );
    if (expiredAction) {
      const timer = window.setTimeout(() => void refreshPlan(true), 1200);
      return () => window.clearTimeout(timer);
    }
  }, [now, plan, refreshPlan]);

  const handleAction = useCallback(
    async (item: TodayPlanItemDto, skipQuestClaimWarning = false) => {
      if (!plan || workingActionId || (item.expiresAt && new Date(item.expiresAt).getTime() <= Date.now())) {
        return;
      }

      if (item.action === TodayPlanAction.ClaimQuest && !skipQuestClaimWarning) {
        let latestProgress = questChestProgress;
        if (!latestProgress?.hasClaimedQuestToday) {
          try {
            latestProgress = await refreshQuestChestProgress();
          } catch {
            toast.error(t('COMPONENTS.TODAY_PLAN.ACTION_ERROR'));
            return;
          }
        }
        if (latestProgress.hasClaimedQuestToday) {
          setQuestClaimToConfirm(item);
          return;
        }
      }

      pushRepeatableDataLayerEvent(GtmEventName.TodayPlanCtaClick, getAnalyticsParameters(plan, item));
      setWorkingActionId(item.id);

      try {
        let didResumeOrClaim = false;
        if (item.action === TodayPlanAction.ClaimQuest) {
          const questId = Number(item.sourceEntity.id);
          const response = await claimQuestAndRefresh(questId);
          presentQuestClaimOutcomes(response);
          await Promise.all([refreshQuestChestProgress(), refreshAccountDetails()]);
          didResumeOrClaim = true;
        } else if (item.action === TodayPlanAction.ClaimChest) {
          const response = await questApi.claimQuestChest();
          if (response) {
            presentQuestClaimOutcomes({
              rewardProgress: response.rewardProgress,
              levelProgress: response.levelProgress,
            });
          }
          await Promise.all([refreshQuests(), refreshQuestChestProgress(), refreshAccountDetails()]);
          didResumeOrClaim = true;
        } else if (item.action === TodayPlanAction.ResumeGame) {
          const sessionUid = item.supportingValues.sessionUid;
          if (typeof sessionUid !== 'string') {
            throw new Error('Missing game session.');
          }
          const verisoulSessionId = await getSessionId();
          const response = await gameApi.continueGame(sessionUid, verisoulSessionId ?? undefined);
          if (response.gameContinueStatus !== GameContinueStatus.Success || !response.entryLink) {
            throw new Error('Game cannot be resumed.');
          }
          openEntryLink(response.entryLink, true);
          didResumeOrClaim = true;
        } else if (item.action === TodayPlanAction.ResumeDeal) {
          const sessionUid = item.supportingValues.sessionUid;
          if (typeof sessionUid !== 'string') {
            throw new Error('Missing deal session.');
          }
          const response = await dealApi.continueDeal(sessionUid);
          openEntryLink(response.entryLink, true);
          didResumeOrClaim = true;
        } else if (item.action === TodayPlanAction.ProtectStreak) {
          onProtectStreak();
        } else {
          navigate(item.deepLink);
        }

        if (didResumeOrClaim) {
          pushRepeatableDataLayerEvent(GtmEventName.TodayPlanActionSuccess, getAnalyticsParameters(plan, item));
        }

        if (item.action === TodayPlanAction.ClaimQuest || item.action === TodayPlanAction.ClaimChest) {
          await refreshPlan(true);
        }
      } catch {
        toast.error(t('COMPONENTS.TODAY_PLAN.ACTION_ERROR'));
        if (item.action === TodayPlanAction.ResumeGame || item.action === TodayPlanAction.ResumeDeal) {
          navigate(item.deepLink);
        }
      } finally {
        setWorkingActionId(null);
        if (skipQuestClaimWarning) {
          setQuestClaimToConfirm(null);
        }
      }
    },
    [
      claimQuestAndRefresh,
      getAnalyticsParameters,
      getSessionId,
      navigate,
      onProtectStreak,
      plan,
      presentQuestClaimOutcomes,
      questChestProgress,
      refreshAccountDetails,
      refreshPlan,
      refreshQuestChestProgress,
      refreshQuests,
      t,
      workingActionId,
    ]
  );

  const shouldRenderTreatmentSkeleton = isLoading && window.sessionStorage.getItem(LAST_VARIANT_CACHE_KEY) === 'today';
  if (!plan && shouldRenderTreatmentSkeleton) {
    return <TodayPlanSkeleton />;
  }

  if (!plan && hasError) {
    return (
      <section className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="min-w-0">
          <p className="font-manrope text-sm font-bold text-slate-800">{t('COMPONENTS.TODAY_PLAN.ERROR_TITLE')}</p>
          <p className="font-manrope text-xs text-slate-500">{t('COMPONENTS.TODAY_PLAN.ERROR_BODY')}</p>
        </div>
        <button
          type="button"
          onClick={() => void refreshPlan(false)}
          className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 font-manrope text-xs font-bold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-200"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t('COMPONENTS.TODAY_PLAN.RETRY')}
        </button>
      </section>
    );
  }

  // Control users and users with no known state keep the existing Home catalog unchanged.
  if (!plan || plan.experiment.variant !== 'today' || plan.actions.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="today-plan-title"
      className="w-full overflow-hidden rounded-2xl border border-[#dfe5f5] bg-white shadow-[0_12px_35px_rgba(33,53,105,0.08)]"
    >
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-gradient-to-r from-[#f6f8ff] to-white px-4 py-3 md:px-5">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#3f54a4] text-white shadow-sm">
            <Target className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h2 id="today-plan-title" className="font-zillaSlab text-lg font-semibold leading-none text-slate-900">
              {t('COMPONENTS.TODAY_PLAN.TITLE')}
            </h2>
            <p className="mt-1 font-manrope text-[11px] text-slate-500">{t('COMPONENTS.TODAY_PLAN.SUBTITLE')}</p>
          </div>
        </div>
        {plan.isPartial && (
          <span
            title={t('COMPONENTS.TODAY_PLAN.PARTIAL_BODY')}
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-1 font-manrope text-[9px] font-extrabold uppercase tracking-wider text-amber-700 ring-1 ring-amber-200"
          >
            <RefreshCw className="h-3 w-3" />
            {t('COMPONENTS.TODAY_PLAN.PARTIAL')}
          </span>
        )}
      </div>

      <div className="divide-y divide-slate-100">
        {plan.actions.map((item, index) => {
          const isExpired = Boolean(item.expiresAt && new Date(item.expiresAt).getTime() <= now);
          const isCompleting = completingActionIds.has(item.id);
          const isWorking = workingActionId === item.id;
          const supportingText = getSupportingText(item, t, formatPoints);
          const title = t(item.titleKey, item.supportingValues);

          return (
            <article
              key={item.id}
              className={classNames(
                'grid min-h-[72px] grid-cols-[40px_minmax(0,1fr)] items-center gap-x-3 px-4 py-3 transition-all duration-500 md:grid-cols-[40px_minmax(0,1fr)_auto] md:px-5',
                {
                  'translate-x-3 scale-[0.98] opacity-0': isCompleting,
                  'bg-slate-50 opacity-65': isExpired,
                  'bg-white': !isExpired && index > 0,
                  'bg-gradient-to-r from-[#fbfcff] to-white': !isExpired && index === 0,
                }
              )}
            >
              <div
                className={classNames(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 transition',
                  isCompleting
                    ? 'bg-emerald-100 text-emerald-700 ring-emerald-200'
                    : index === 0
                      ? 'bg-[#eef1ff] text-[#3f54a4] ring-[#dfe5ff]'
                      : 'bg-slate-50 text-slate-600 ring-slate-200'
                )}
              >
                {isCompleting ? <Check className="h-5 w-5" /> : getActionIcon(item.action)}
              </div>

              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <h3 className="min-w-0 font-manrope text-sm font-extrabold leading-5 text-slate-900">{title}</h3>
                  {item.expiresAt && (
                    <span
                      className={classNames(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-manrope text-[9px] font-extrabold ring-1',
                        isExpired
                          ? 'bg-slate-100 text-slate-500 ring-slate-200'
                          : 'bg-amber-50 text-amber-700 ring-amber-200'
                      )}
                    >
                      <Clock3 className="h-3 w-3" />
                      {getCountdown(item.expiresAt, now, t('COMPONENTS.TODAY_PLAN.EXPIRED'))}
                    </span>
                  )}
                </div>
                {supportingText && (
                  <p className="mt-0.5 flex items-center gap-1 font-manrope text-[11px] leading-4 text-slate-500">
                    {(item.action === TodayPlanAction.ClaimQuest ||
                      item.action === TodayPlanAction.ClaimChest ||
                      item.action === TodayPlanAction.ViewReward) && <Coins className="h-3 w-3 shrink-0" />}
                    {supportingText}
                  </p>
                )}
              </div>

              <button
                type="button"
                disabled={isExpired || Boolean(workingActionId)}
                onClick={() => void handleAction(item)}
                className={classNames(
                  'col-start-2 mt-2 inline-flex min-h-9 w-fit items-center justify-center gap-1.5 rounded-xl font-manrope text-xs font-extrabold transition focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-50 md:col-start-3 md:row-start-1 md:mt-0',
                  index === 0
                    ? 'bg-[#3f54a4] px-4 text-white shadow-sm hover:bg-[#344690]'
                    : 'px-2 text-[#3f54a4] hover:bg-[#f4f6ff]'
                )}
              >
                {isWorking ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : isCompleting ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <>
                    {t(item.ctaKey)}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </>
                )}
              </button>
            </article>
          );
        })}
      </div>
      {plan.questProgressSummary && plan.questProgressSummary.activeCount > 0 && (
        <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3 md:px-5">
          <div className="flex items-center justify-between gap-3 font-manrope text-[10px] font-extrabold">
            <span className="uppercase tracking-[0.14em] text-slate-500">
              {t('COMPONENTS.TODAY_PLAN.QUEST_PROGRESS.TITLE')}
            </span>
            <span className="text-[#3f54a4]">
              {t('COMPONENTS.TODAY_PLAN.QUEST_PROGRESS.SUMMARY', {
                progress: plan.questProgressSummary.totalProgress,
                needed: plan.questProgressSummary.totalNeeded,
                completed: plan.questProgressSummary.completedCount,
              })}
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#3f54a4] to-blue-400 transition-all"
              style={{
                width: `${Math.min(
                  100,
                  Math.round(
                    (plan.questProgressSummary.totalProgress / Math.max(1, plan.questProgressSummary.totalNeeded)) * 100
                  )
                )}%`,
              }}
            />
          </div>
        </div>
      )}
      <QuestClaimWarningModal
        isOpen={Boolean(questClaimToConfirm)}
        isConfirming={Boolean(questClaimToConfirm && workingActionId === questClaimToConfirm.id)}
        onCancel={() => setQuestClaimToConfirm(null)}
        onConfirm={() => {
          if (questClaimToConfirm) {
            void handleAction(questClaimToConfirm, true);
          }
        }}
      />
    </section>
  );
};

export default TodayPlan;
